import { Injector, inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CompanyService } from '../services/company.service';
import { awaitSessionRestore } from './await-session';

/**
 * Guard for the `/` route: redirects to `/<company-slug>/campaigns` for the
 * default company, or to `/login` if unauthenticated / no companies.
 *
 * Waits for session restore via a signal→observable bridge rather than a timer,
 * so a cancelled navigation issues no late `router.navigate(...)` on a stale
 * context. See `awaitSessionRestore`.
 */
export const homeGuard: CanActivateFn = async () => {
	const auth = inject(AuthService);
	const router = inject(Router);
	const companyService = inject(CompanyService);
	const injector = inject(Injector);

	await awaitSessionRestore(auth, injector);

	if (!auth.isAuthenticated()) {
		router.navigate(['/login']);
		return false;
	}

	// Ensure companies are loaded. `loadCompanies()` returns a shared Observable
	// (via shareReplay) so concurrent callers share one HTTP request; `firstValueFrom`
	// subscribes and thus triggers the cold Observable.
	if (companyService.companies().length === 0) {
		await firstValueFrom(companyService.loadCompanies());
	}

	const target = companyService.pickDefaultCompany();
	if (target) {
		router.navigate(['/', target.slug, 'campaigns']);
	} else {
		// No companies returned — logged-in but unusable. Fail closed to login.
		router.navigate(['/login']);
	}
	return false;
};
